import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/AdminLayout';
import axiosInstance from '../../lib/axiosInstance';
import { toast } from 'react-toastify';
import { MdAdd, MdMessage, MdChevronRight } from 'react-icons/md';

const MySupportTickets = () => {
    const navigate = useNavigate();
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/brand/support/tickets');
            if (response.data.success) {
                setTickets(response.data.tickets);
            }
        } catch (error) {
            console.error('Error fetching tickets:', error);
            // toast.error('Failed to fetch tickets');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'open': return 'bg-emerald-100 text-emerald-700';
            case 'in_progress': return 'bg-amber-100 text-amber-700';
            case 'closed': return 'bg-slate-100 text-slate-500';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-4 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
                <div>
                    <h1 className="text-4xl font-black text-[var(--text)] tracking-tighter">Support Center</h1>
                    <p className="text-[var(--muted)] font-medium">Manage your brand support inquiries and tickets.</p>
                </div>
                <button 
                    onClick={() => navigate('/support/new')}
                    className="flex items-center justify-center gap-2 bg-[var(--text)] text-[var(--bg)] px-8 py-4 rounded-3xl font-black hover:bg-blue-600 transition shadow-lg shadow-blue-500/10"
                >
                    <MdAdd size={24}/> Raise New Ticket
                </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {loading ? (
                    <div className="p-20 text-center animate-pulse flex flex-col items-center gap-4">
                        <div className="w-12 h-12 bg-[var(--surface)] rounded-full"></div>
                        <div className="text-[var(--muted)] font-bold uppercase tracking-widest text-xs">Fetching Records...</div>
                    </div>
                ) : tickets.length === 0 ? (
                    <div className="bg-[var(--surface)] border-2 border-dashed border-[var(--border)] rounded-[40px] p-20 text-center flex flex-col items-center gap-6 shadow-xl shadow-slate-200/20 dark:shadow-blue-900/5">
                        <div className="w-20 h-20 bg-[var(--bg)] rounded-full flex items-center justify-center text-[var(--muted)]">
                            <MdMessage size={40}/>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-[var(--text)]">No active tickets</h3>
                            <p className="text-[var(--muted)] text-sm mt-1">When you raise a support ticket, it will appear here.</p>
                        </div>
                    </div>
                ) : (
                    tickets.map((ticket) => (
                        <div 
                            key={ticket.id}
                            onClick={() => navigate(`/support/details/${ticket.ticket_no}`)}
                            className="group bg-[var(--surface)] border border-[var(--border)] p-6 rounded-[32px] hover:border-blue-500/30 hover:shadow-xl hover:shadow-blue-500/5 transition cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4"
                        >
                            <div className="flex items-center gap-5">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center font-black text-sm ${getStatusColor(ticket.status)}`}>
                                    {ticket.status === 'open' ? 'OP' : ticket.status === 'in_progress' ? 'IP' : 'CL'}
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-xs font-bold text-blue-600 tracking-tighter">{ticket.ticket_no}</span>
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                            ticket.priority === 'high' ? 'bg-red-100 text-red-600' : 'bg-[var(--bg)] text-[var(--muted)]'
                                        }`}>
                                            {ticket.priority}
                                        </span>
                                        {ticket.has_unread_by_raiser === 1 && (
                                            <span className="bg-blue-600 text-white px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider animate-pulse">
                                                New Message
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-lg font-bold text-[var(--text)] group-hover:text-blue-600 transition mt-1 tracking-tight">
                                        {JSON.parse(ticket.topic_path).pop()?.label}
                                    </h3>
                                    <p className="text-xs text-[var(--muted)] font-medium truncate max-w-[300px] mt-0.5">
                                        {ticket.comment}
                                    </p>
                                </div>
                            </div>
                            
                            <div className="flex items-center justify-between md:justify-end gap-8 border-t border-[var(--border)] md:border-t-0 pt-4 md:pt-0">
                                <div className="flex flex-col items-end">
                                    <span className="text-[10px] font-black text-[var(--muted)] uppercase tracking-widest">LAST UPDATED</span>
                                    <span className="text-xs font-bold text-[var(--text)]">{new Date(ticket.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                                </div>
                                <MdChevronRight size={24} className="text-[var(--muted)] group-hover:text-blue-500 transition translate-x-0 group-hover:translate-x-1" />
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default MySupportTickets;
